// ─────────────────────────────────────────────────────────────────────────────
// Export column registries — the single source of truth for what can be exported.
//
// Each column is { key, label, group, get(row) }:
//   key   — stable identifier, logged in export_logs.columns_json
//   label — the Excel header shown to the user
//   group — the section it appears under in the export picker
//   get   — pulls the value out of that page's row object
//
// `DEFAULT_KEYS` lists the columns pre-checked in the picker; anything omitted is
// available but unchecked. Labels/groups are kept consistent across contexts so
// the same concept reads the same in every export.
// ─────────────────────────────────────────────────────────────────────────────

// Section order for rendering (groups not listed fall to the end, in first-seen order)
export const GROUP_ORDER = ["Identity", "Location", "Contact", "Event Intelligence", "Review", "Visit Sheet", "Data Quality"];

// Company Master (CompaniesPage rows — see mapCompany, which spreads the raw record)
export const COMPANY_MASTER_COLUMNS = [
  // Identity
  { key: "name",       label: "Company",       group: "Identity", get: (c) => c.name },
  { key: "website",    label: "Website",       group: "Identity", get: (c) => c.website || "" },
  { key: "type",       label: "Company Type",  group: "Identity", get: (c) => c.type || "" },
  { key: "iteStatus",  label: "Exhibitor Status", group: "Identity", get: (c) => c.iteStatus || "" },
  // Location
  { key: "country",       label: "Country",        group: "Location", get: (c) => c.country },
  { key: "countryStatus", label: "Country Status", group: "Location", get: (c) => c.countryStatus || "" },
  { key: "city",          label: "HQ City",        group: "Location", get: (c) => c.hq_city || "" },
  { key: "region",        label: "Region",         group: "Location", get: (c) => c.region || "" },
  // Contact
  { key: "email",      label: "Email",             group: "Contact", get: (c) => c.email || "" },
  { key: "phone",      label: "Phone",             group: "Contact", get: (c) => c.phone || "" },
  { key: "linkedin",   label: "LinkedIn",          group: "Contact", get: (c) => c.linkedin_page || "" },
  { key: "address",    label: "Physical Address",  group: "Contact", get: (c) => c.physical_address || "" },
  // Event Intelligence
  { key: "event",       label: "Target ITE Event",          group: "Event Intelligence", get: (c) => c.primaryEventName || "" },
  { key: "eventSector", label: "Target Event Sector",       group: "Event Intelligence", get: (c) => c.primary_event_category || "" },
  { key: "eventFit",    label: "Event Fit",                 group: "Event Intelligence", get: (c) => c.eventFit },
  { key: "eventFitReason", label: "Event Fit Reasoning",    group: "Event Intelligence", get: (c) => c.eventFitReason || "" },
  { key: "exportReady", label: "Export Readiness",          group: "Event Intelligence", get: (c) => c.exportReady },
  { key: "exportReadinessLogic", label: "Export Readiness Logic", group: "Event Intelligence", get: (c) => c.exportReadinessLogic || "" },
  { key: "priority",    label: "Priority Score",            group: "Event Intelligence", get: (c) => c.priority },
  { key: "crossEvents", label: "Cross-Event Opportunities", group: "Event Intelligence", get: (c) => c.crossEventOpportunities || (c.secondaryEventNames || []).join(" | ") },
  { key: "crossEventReasoning", label: "Cross-Event Reasoning", group: "Event Intelligence", get: (c) => c.crossEventReasoning || "" },
  { key: "pastEvents", label: "Past Events",                group: "Event Intelligence", get: (c) => c.pastEvents || "" },
  // Data Quality
  { key: "completeness", label: "Profile Completeness %",   group: "Data Quality", get: (c) => c.completeness },
];

// Leads review — unified columns for both Sales and Marketing Manager exports.
// Contact fields (email, phone, linkedin, address) are mapped in normalizeCompany
// from the /companies list response, which already returns them from company_firmographics.
// Visit Sheet fields are blank printable columns available for Sales; off by default.
export const LEADS_COLUMNS = [
  // Identity
  { key: "name",         label: "Company",          group: "Identity",           get: (c) => c.name },
  { key: "website",      label: "Website",          group: "Identity",           get: (c) => c.website || "" },
  { key: "type",         label: "Company Type",     group: "Identity",           get: (c) => c.type || "" },
  { key: "iteStatus",    label: "Exhibitor Status", group: "Identity",           get: (c) => c.iteStatus || "" },
  // Location
  { key: "country",      label: "Country",          group: "Location",           get: (c) => c.country },
  { key: "city",         label: "HQ City",          group: "Location",           get: (c) => c.city || "" },
  { key: "region",       label: "Region",           group: "Location",           get: (c) => c.region || "" },
  // Contact
  { key: "email",        label: "Email",            group: "Contact",            get: (c) => c.email || "" },
  { key: "phone",        label: "Phone",            group: "Contact",            get: (c) => c.phone || "" },
  { key: "linkedin",     label: "LinkedIn",         group: "Contact",            get: (c) => c.linkedin || "" },
  { key: "address",      label: "Physical Address", group: "Contact",            get: (c) => c.address || "" },
  // Event Intelligence
  { key: "category",     label: "Category",         group: "Event Intelligence", get: (c) => c.category },
  { key: "eventFit",     label: "Event Fit",        group: "Event Intelligence", get: (c) => c.eventFit },
  { key: "exportReady",  label: "Export Readiness", group: "Event Intelligence", get: (c) => c.exportReady },
  { key: "priority",     label: "Priority Score",   group: "Event Intelligence", get: (c) => c.priority },
  // Review
  { key: "crmStatus",    label: "CRM Status",       group: "Review",             get: (c) => c.crmStatus || "Not in CRM" },
  { key: "reviewStatus", label: "Review Status",    group: "Review",             get: (c) => c.reviewStatus },
  { key: "note",         label: "Note",             group: "Review",             get: (c) => c.note || c.rejectReason || "" },
  // Visit Sheet — blank printable columns for walk-the-floor use; available but off by default
  { key: "hallStand",    label: "Hall / Stand",     group: "Visit Sheet",        get: () => "" },
  { key: "metYN",        label: "Met? (Y/N)",       group: "Visit Sheet",        get: () => "" },
  { key: "contactMet",   label: "Contact Met",      group: "Visit Sheet",        get: () => "" },
  { key: "followUp",     label: "Follow-up",        group: "Visit Sheet",        get: () => "" },
];

// CRM send queue (SendPage approved companies)
export const SEND_COLUMNS = [
  { key: "name",       label: "Company",     group: "Identity",           get: (c) => c.name },
  { key: "country",    label: "Country",     group: "Location",           get: (c) => c.country },
  { key: "eventFit",   label: "Event Fit",   group: "Event Intelligence", get: (c) => c.eventFit ?? c.fitScore ?? "" },
  { key: "priority",   label: "Priority",    group: "Event Intelligence", get: (c) => c.priority },
  { key: "decision",   label: "Decision",    group: "Review",             get: (c) => c.salesDecision ?? "" },
  { key: "sendStatus", label: "Send Status", group: "Review",             get: (c) => c.sendStatus ?? "Pending" },
];

// Growth Analysis — one row per year (GrowthPage byYear series)
export const GROWTH_COLUMNS = [
  { key: "y",          label: "Year",               group: "Metrics", get: (d) => d.y },
  { key: "total",      label: "Total Companies",    group: "Metrics", get: (d) => d.total },
  { key: "newCo",      label: "New Companies",      group: "Metrics", get: (d) => d.newCo },
  { key: "returning",  label: "Returning",          group: "Metrics", get: (d) => d.returning },
  { key: "dropped",    label: "Dropped",            group: "Metrics", get: (d) => d.dropped },
  { key: "countries",  label: "Countries",          group: "Metrics", get: (d) => d.countries },
  { key: "categories", label: "Product Categories", group: "Metrics", get: (d) => d.categories },
];

// Default checked columns per registry (by key). Omitted = available but unchecked.
export const DEFAULT_KEYS = {
  companyMaster: ["name", "country", "countryStatus", "email", "phone", "event", "eventSector", "eventFit", "eventFitReason", "exportReady", "exportReadinessLogic", "priority", "crossEvents", "crossEventReasoning", "pastEvents", "completeness"],
  leadsReview:   ["name", "country", "email", "phone", "linkedin", "category", "eventFit", "exportReady", "priority", "reviewStatus", "note"],
  send:          SEND_COLUMNS.map((c) => c.key),
  growth:        GROWTH_COLUMNS.map((c) => c.key),
};
